/**
 * The support chat as a small window at the bottom right: a round starter
 * button, and opened, a panel with the practice's requests on the left and a
 * conversation (or a new request) on the right. Presentation ported from the
 * design prototype: text, images and screenshots (with an editor to cut out and
 * mark up), dictation, and the team's notes in the thread. State and wiring:
 * SupportProvider.
 *
 * Design notes from the prototype:
 *  - Next to the page, not instead of it: a conversation with support is a side
 *    matter next to the work. Opened, the window survives every page change
 *    (the state lives above the routes); collapsed it is the round button with
 *    the number of requests that have news.
 *  - The width follows the screen, the height does not: more width means fewer
 *    line breaks and more conversation at a glance, without growing taller.
 *  - Write at once, like in a chat app: the composer is ready as soon as the
 *    window opens, and sending opens a new request. Earlier requests stay
 *    visible as a column on the left, "Neue Anfrage" below them.
 *  - Feedback like a messenger: own messages right and blue, the team left and
 *    white with the name in the first bubble; time and ticks inside the bubble
 *    (one grey tick = delivered, two blue = read).
 *  - Several people of a practice write in the same request: your own messages
 *    carry no name ("Sie" in the list), a colleague's carry the colleague's
 *    name, on the practice side (right) as well.
 *  - Showing beats describing: a screenshot of this screen, cut to the part that
 *    matters before it leaves the computer. Images also come in by pasting and
 *    by dropping them anywhere on the page.
 *  - Dictation writes into the field and never sends: what was said can be read
 *    and corrected first.
 *
 * Every image and dictation control follows the server's features (an older
 * backend offers neither, so they are not there). Dictation also needs a
 * browser that can record; the screenshot button one that can capture the
 * screen, otherwise the "+" file picker takes its place.
 *
 * Opened from elsewhere by a window event (the Tasks page's row button):
 *   window.dispatchEvent(new CustomEvent('deskbot-support-open',
 *     { detail: { practiceId, taskId, taskCreatedAt, callerName, phone } }))
 * An open request about that task is shown again; otherwise a new request
 * starts with the task card.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useSupport } from './SupportProvider';
import {
  DICTATION_TEXT,
  MAX_MESSAGE_LENGTH,
  SUPPORT_TEXT,
  UPLOAD_TEXT,
  previewLine,
  referenceFromOpenEvent,
} from './supportAdapter';
import { teamPhoto } from './teamPhotos';
import { taskLinkPath } from '../../utils/taskLinks';
import {
  ArrowDiagonal, ArrowUp, Bubble, Check, CheckCircle, Close, Collapse, Crosshair, Mic, More, Pencil, Plus,
  ReadTicks, ScreenCapture, Undo,
} from './supportIcons';
import './SupportWindow.css';

/* ── Times ─────────────────────────────────────────────────────────────── */

const dayKey = (d) => d.toDateString();
const clockTime = (iso) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

/** For the list: today the time, yesterday "Gestern", otherwise the date. */
const listTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  if (dayKey(d) === dayKey(today)) return clockTime(iso);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(yesterday)) return 'Gestern';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  if (dayKey(d) === dayKey(today)) return 'Heute';
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(yesterday)) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
};

const fullTime = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : undefined);

/* ── Name colours ──────────────────────────────────────────────────────── */

/**
 * Every team member keeps their colour, like names in a group chat, by the same
 * rule as the tracker's avatars (mlUserColor). The narrow red band of the hue
 * circle is skipped: red is the signal colour, and no name should look like a
 * warning.
 */
const nameHue = (name) => {
  let h = 0;
  for (const c of String(name || '')) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h >= 345 || h < 20 ? (h + 30) % 360 : h;
};
const nameColor = (name) => `hsl(${nameHue(name)} 52% 42%)`; // face and initials, like the tracker's avatars
const nameTextColor = (name) => `hsl(${nameHue(name)} 55% 34%)`; // the name above a message, darker to read

/** Header subtitle with the practice the window writes to (users may belong to several). */
const withPractice = (support, text) => [text, support.practiceName].filter(Boolean).join(' · ');

/* ── Scrolling ─────────────────────────────────────────────────────────── */

/**
 * "ist-gerollt" as soon as something lies under the top edge; the edge then
 * fades softly (see CSS). Set on the element directly instead of via state:
 * re-rendering the thread on every scroll event would be expensive.
 */
const markEdge = (el) => {
  if (!el) return;
  el.classList.toggle('ist-gerollt', el.scrollTop > 2);
  // Only while there is something to scroll may the window hold the page behind it (see CSS, data-rollt).
  el.dataset.rollt = el.scrollHeight > el.clientHeight + 1 ? 'ja' : 'nein';
};

/** Measures after every render and on resize whether there is anything to scroll. */
const useScrollEdge = (ref) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => markEdge(el);
    check();
    window.addEventListener('resize', check);
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(check); observer.observe(el); }
    return () => { window.removeEventListener('resize', check); if (observer) observer.disconnect(); };
  });
};

/* ── What the browser can do ───────────────────────────────────────────── */

const PASTE_KEYS = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad/i.test(navigator.userAgentData?.platform || navigator.platform || '') ? '⌘V' : 'Strg+V';

/** Not every browser can capture the screen; then the "+" file picker takes the button's place. */
const canCaptureScreen = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices
  && typeof navigator.mediaDevices.getDisplayMedia === 'function';

/** Dictation needs a recorder and a microphone, whatever the server offers. */
const canRecord = () => typeof window !== 'undefined' && !!window.MediaRecorder
  && typeof navigator !== 'undefined' && !!navigator.mediaDevices
  && typeof navigator.mediaDevices.getUserMedia === 'function';

/* ── Screenshot ────────────────────────────────────────────────────────── */

const SCREENSHOT_FAILED = 'Das Bildschirmfoto konnte nicht aufgenommen werden.';

/**
 * A screenshot of this tab as a PNG blob (null if no frame could be drawn).
 * Throws when the share dialog is cancelled or the capture fails. `panel` (the
 * support window) is hidden for the moment of the picture.
 */
const captureTab = async (panel) => {
  let stream = null;
  try {
    // Ask for more than the tab can give, so the browser never rescales. It never
    // scales up (in a small window 7680 asked gives the real size), while asking
    // too little made it scale down by odd factors (1.5), which smears every
    // pixel. devicePixelRatio is no guide: it can report 1 for a tab drawn twice
    // as fine. Not fixable from a page: frames arrive as I420, i.e. with half the
    // colour resolution; brightness, and with it text, is complete.
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: 'browser',
        width: { ideal: 7680 },
        height: { ideal: 4320 },
        frameRate: { ideal: 8, max: 15 },
      },
      audio: false,
      preferCurrentTab: true,
      surfaceSwitching: 'exclude',
    });
    // Keep the mouse pointer out of the picture. Three measures, and none of them
    // switches another off: restrictTo() reports success even where it is not
    // applied, so its promise proves nothing.
    // 1. cursor: none on the whole page (.sw-ohne-zeiger): capturing its own tab,
    //    Chrome draws the pointer the page sets.
    document.documentElement.classList.add('sw-ohne-zeiger');
    // 2. Element Capture where the browser has it: limits the capture to the
    //    rendered page, which also keeps a window next to it (the PVS) out.
    const track = stream.getVideoTracks()[0];
    try {
      if (window.RestrictionTarget?.fromElement && typeof track?.restrictTo === 'function') {
        await track.restrictTo(await window.RestrictionTarget.fromElement(document.documentElement));
      }
    } catch { /* costs nothing to try and can only help */ }

    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // Hide the window FIRST, then wait for new frames: the capture delivers one
    // about every 33 ms, and two paint steps still photographed the window. Every
    // wait has a limit: a still screen delivers no new frames at all, and a
    // window that never comes back is worse than a picture taken too early.
    const nextFrame = (limit) => new Promise((resolve) => {
      let done = false;
      const once = () => { if (!done) { done = true; resolve(); } };
      if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(once);
      setTimeout(once, limit);
    });
    await nextFrame(400);
    if (panel) panel.style.visibility = 'hidden';

    // 3. Wait until the mouse rests, ALWAYS: in a tab capture Chrome draws the
    //    pointer while it moves or has just moved, and the click on the camera IS
    //    such a movement, a tenth of a second ago.
    await new Promise((resolve) => {
      let timer = null;
      const finish = () => {
        clearTimeout(timer); clearTimeout(brake); window.removeEventListener('pointermove', moved); resolve();
      };
      const moved = () => { clearTimeout(timer); timer = setTimeout(finish, 900); };
      const brake = setTimeout(finish, 2500);
      window.addEventListener('pointermove', moved);
      timer = setTimeout(finish, 900);
    });

    await nextFrame(150);
    await nextFrame(150);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    video.pause();
    video.srcObject = null;
    return blob;
  } finally {
    // The pointer MUST come back, whatever went wrong above: otherwise the
    // practice sits in front of a page without a mouse pointer.
    document.documentElement.classList.remove('sw-ohne-zeiger');
    if (panel) panel.style.visibility = '';
    if (stream) stream.getTracks().forEach((t) => t.stop());
  }
};

/* ── Image editor ──────────────────────────────────────────────────────── */

const TOOLS = [
  { id: 'region', label: 'Bereich', Icon: Crosshair, hint: 'Bereich mit gedrückter Maustaste aufziehen' },
  { id: 'pen', label: 'Stift', Icon: Pencil, hint: 'Mit gedrückter Maustaste zeichnen' },
  { id: 'arrow', label: 'Pfeil', Icon: ArrowDiagonal, hint: 'Pfeil auf die Stelle ziehen' },
];

/** Stroke width relative to the image: on a 3440px wide screenshot 3px would be a hair. */
const strokeWidth = (w) => Math.max(3, Math.round(w / 260));

// [colour, width] per layer of a stroke, the same for preview and result: a white
// seam under the red, which alone disappears on dark surfaces.
const strokeLayers = (width) => [['rgba(255,255,255,.92)', width + Math.max(2, width)], ['#ff3b30', width]];

/**
 * A stroke as an SVG path, in real image pixels. The SAME string draws the
 * preview (<path d>) and the result (Path2D on the canvas): what was drawn is
 * exactly what arrives.
 */
const strokePath = (stroke, w, h) => {
  const p = stroke.points.map((q) => ({ x: q.x * w, y: q.y * h }));
  if (stroke.tool === 'arrow') {
    const a = p[0];
    const b = p[p.length - 1];
    const head = Math.max(14, strokeWidth(w) * 5);
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const barb = (v) => `${b.x - head * Math.cos(angle + v)} ${b.y - head * Math.sin(angle + v)}`;
    return `M ${a.x} ${a.y} L ${b.x} ${b.y}`
      + ` M ${b.x} ${b.y} L ${barb(-Math.PI / 7)}`
      + ` M ${b.x} ${b.y} L ${barb(Math.PI / 7)}`;
  }
  return p.map((q, i) => `${i ? 'L' : 'M'} ${q.x} ${q.y}`).join(' ');
};

/**
 * One editor for everything that happens to the image: cut out a region, draw,
 * set an arrow, with the tools visible from the start and a clear "Übernehmen"
 * at the bottom. It opens right after a screenshot and again from a thumbnail
 * ("Bearbeiten").
 *
 * The cut matters twice: the browser always delivers the WHOLE tab, and a
 * 3440px screenshot that points at one cell is unreadable; and what lies outside
 * the cut never leaves the computer (whoever cuts out the calendar does not send
 * the patient list next to it along).
 *
 * Releasing the mouse sets the selection instead of closing, since drawing and
 * correcting follow; Enter confirms, Esc cancels. Coordinates are fractions
 * (0…1) of the image, never screen points, so a stroke lands where it was drawn
 * on any image size.
 *
 * Rendered into document.body, so the full-screen overlay does not depend on the
 * panel's stacking context or its opening animation.
 */
const ImageEditor = ({ url, onDone, onCancel }) => {
  const image = useRef(null);
  const [tool, setTool] = useState('region');
  const [region, setRegion] = useState(null); // fractions {x, y, w, h}; null means the whole image
  const [strokes, setStrokes] = useState([]);
  const [drag, setDrag] = useState(null);
  const [pointer, setPointer] = useState(null); // for the crosshair
  const [size, setSize] = useState(null); // the real image size, known once it has loaded
  const [busy, setBusy] = useState(false);

  const fraction = (e) => {
    const r = image.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const rectOf = (a, b) => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });

  const down = (e) => {
    if (busy) return;
    e.preventDefault();
    image.current.setPointerCapture?.(e.pointerId);
    const p = fraction(e);
    setDrag({ tool, points: [p, p] });
  };
  const move = (e) => {
    const p = fraction(e);
    setPointer(p);
    if (drag) setDrag((d) => d && { ...d, points: d.tool === 'pen' ? [...d.points, p] : [d.points[0], p] });
  };
  const up = () => {
    if (!drag) return;
    setDrag(null);
    const a = drag.points[0];
    const b = drag.points[drag.points.length - 1];
    // A click without dragging is no input: otherwise every slipped click puts a dot in the picture.
    if (drag.tool === 'region') {
      const r = rectOf(a, b);
      if (r.w > 0.02 && r.h > 0.02) setRegion(r);
    } else if (drag.tool === 'pen' ? drag.points.length > 2 : Math.hypot(b.x - a.x, b.y - a.y) > 0.02) {
      setStrokes((old) => [...old, drag]);
    }
  };

  /** The strokes first, then the selection: undone in the order they were made. */
  const undo = () => {
    if (strokes.length) setStrokes((old) => old.slice(0, -1));
    else setRegion(null);
  };

  const apply = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
      const W = img.naturalWidth;
      const H = img.naturalHeight;
      const r = region || { x: 0, y: 0, w: 1, h: 1 };
      const sx = Math.round(r.x * W);
      const sy = Math.round(r.y * H);
      const sw = Math.max(16, Math.round(r.w * W));
      const sh = Math.max(16, Math.round(r.h * H));
      const canvas = document.createElement('canvas');
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      // The strokes lie in coordinates of the WHOLE image; the cut moves the origin.
      ctx.translate(-sx, -sy);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const layers = strokeLayers(strokeWidth(W));
      strokes.forEach((s) => {
        const path = new Path2D(strokePath(s, W, H));
        layers.forEach(([color, lineWidth]) => { ctx.strokeStyle = color; ctx.lineWidth = lineWidth; ctx.stroke(path); });
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (blob) onDone(new File([blob], 'bildschirmfoto.png', { type: 'image/png' }));
      else onCancel();
    } catch { onCancel(); } finally { setBusy(false); }
  };

  // Through a ref, so the keys are not registered anew with every stroke. Capture
  // phase, and the key stops here: Enter must not also send the message, nor Esc
  // close something on the page underneath.
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
      else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); applyRef.current(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const current = TOOLS.find((t) => t.id === tool) || TOOLS[0];
  const preview = drag && drag.tool !== 'region' ? [...strokes, drag] : strokes;
  const selection = drag && drag.tool === 'region' ? rectOf(drag.points[0], drag.points[drag.points.length - 1]) : region;
  const box = selection
    ? { left: `${selection.x * 100}%`, top: `${selection.y * 100}%`, width: `${selection.w * 100}%`, height: `${selection.h * 100}%` }
    : null;
  const layers = strokeLayers(size ? strokeWidth(size.w) : 3);
  const showHint = !drag && (tool === 'region' ? !region : !strokes.length);
  const crosshair = tool === 'region' && !!pointer;

  return createPortal(
    <div className="sw-ed" role="dialog" aria-modal="true" aria-label="Bildschirmfoto bearbeiten">
      <div className="sw-ed-leiste" role="toolbar" aria-label="Werkzeuge">
        {TOOLS.map(({ id, label, Icon }) => (
          <button key={id} type="button" className="sw-ed-werkzeug" aria-pressed={tool === id}
            onClick={() => setTool(id)} disabled={busy}>
            <Icon /> {label}
          </button>
        ))}
        <span className="sw-ed-trenner" aria-hidden="true" />
        <button type="button" className="sw-ed-werkzeug" onClick={undo} disabled={busy || (!strokes.length && !region)}>
          <Undo /> Zurück
        </button>
      </div>
      <div className="sw-ed-buehne">
        {/* Never larger than the image itself: the height fills the stage and the aspect ratio gives the
            width, both capped at the real pixels. Without the cap a small capture was blown up to the full
            stage height and went blurry. */}
        <span className="sw-ed-bild" data-kreuz={crosshair ? 'ja' : 'nein'} style={{
          aspectRatio: size ? `${size.w} / ${size.h}` : '16 / 9',
          maxWidth: size ? `min(100%, ${size.w}px)` : '100%',
          maxHeight: size ? `min(100%, ${size.h}px)` : '100%',
        }}>
          <img ref={image} src={url} alt="" draggable={false}
            onLoad={(e) => setSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
            onPointerLeave={() => setPointer(null)} />
          {/* The order says something here. The strokes lie UNDER the dimming, so an arrow reaching half
              out of the selection looks half dimmed there, because it really is cut off. Above it, it would
              shine in full and promise what the finished image does not keep. Crosshair and frame lie above
              everything: they are controls, not image. */}
          {size && preview.length > 0 && (
            <svg className="sw-ed-striche" viewBox={`0 0 ${size.w} ${size.h}`} aria-hidden="true">
              {preview.map((s, i) => {
                const d = strokePath(s, size.w, size.h);
                return (
                  <g key={i} fill="none" strokeLinecap="round" strokeLinejoin="round">
                    {layers.map(([color, lineWidth]) => <path key={color} d={d} stroke={color} strokeWidth={lineWidth} />)}
                  </g>
                );
              })}
            </svg>
          )}
          {box && (
            <span className="sw-ed-schatten" aria-hidden="true">
              <i style={{ left: 0, right: 0, top: 0, height: box.top }} />
              <i style={{ left: 0, width: box.left, top: box.top, height: box.height }} />
              <i style={{ left: `calc(${box.left} + ${box.width})`, right: 0, top: box.top, height: box.height }} />
              <i style={{ left: 0, right: 0, top: `calc(${box.top} + ${box.height})`, bottom: 0 }} />
            </span>
          )}
          {box && <span className="sw-ed-rahmen" style={box} />}
          {crosshair && (
            <svg className="sw-ed-kreuz" aria-hidden="true" viewBox="-13 -13 26 26"
              style={{ left: `${pointer.x * 100}%`, top: `${pointer.y * 100}%` }}>
              <path d="M-11 0H11M0-11V11" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="3" />
              <path d="M-11 0H11M0-11V11" fill="none" stroke="#12161d" strokeWidth="1" />
            </svg>
          )}
          {showHint && <span className="sw-ed-hinweis">{current.hint}</span>}
        </span>
      </div>
      <div className="sw-ed-fuss">
        <span className="sw-ed-links">
          <button type="button" onClick={onCancel}>Abbrechen (Esc)</button>
          {region && <button type="button" onClick={() => setRegion(null)}>Ganzes Bild</button>}
        </span>
        <button type="button" className="ist-haupt" onClick={apply} disabled={busy}>
          {busy ? 'Einen Moment …' : 'Übernehmen'}
        </button>
      </div>
    </div>,
    document.body,
  );
};

/* ── Composer ──────────────────────────────────────────────────────────── */

const IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif';
const isImage = (file) => String(file?.type || '').startsWith('image/') || /\.(heic|heif)$/i.test(file?.name || '');
const thumbKey = () => `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const THUMB_STATE = { uploading: 'ist-laedt', done: 'ist-fertig', failed: 'ist-fehler' };
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** Stops everything a recording holds: timer, level meter, audio context, recorder, microphone. */
const releaseRecording = (rec) => {
  clearInterval(rec.timer);
  if (rec.frame) cancelAnimationFrame(rec.frame);
  if (rec.audio && rec.audio.state !== 'closed') rec.audio.close().catch(() => {});
  try { if (rec.recorder.state !== 'inactive') rec.recorder.stop(); } catch { /* already stopped */ }
  rec.stream.getTracks().forEach((t) => t.stop());
};

/**
 * The composer. Enter sends, Shift+Enter makes a new line, the field grows up
 * to 120px. `onSend(text, attachmentIds)` resolves to {ok} or {ok: false,
 * error}; a failure keeps the text and the images for another try and says why.
 *
 * Images come in by the screenshot button (or "+"), by pasting and by dropping;
 * each one uploads at once and waits as a thumbnail that can be removed or
 * opened in the editor again. A message may be images only.
 */
const Composer = ({ onSend, sending, placeholder, autoFocus = false }) => {
  const { features = {}, uploadImage, dictate } = useSupport() || {};
  const { maxImages, maxDictationSeconds: maxSeconds } = features; // both set by adaptFeatures
  const imagesOn = !!features.images && !!uploadImage;
  const dictationOn = !!features.dictation && !!dictate && canRecord();

  const [text, setText] = useState('');
  const [notice, setNotice] = useState('');
  // Thumbnails: {key, preview (local object URL), status 'uploading' | 'done' | 'failed', attachment, reason}.
  const [images, setImages] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [capture, setCapture] = useState(null); // a fresh screenshot (object URL) while its region is chosen
  const [editing, setEditing] = useState(null); // {key, url}: a thumbnail opened in the editor again
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [converting, setConverting] = useState(false);
  const [focusTick, setFocusTick] = useState(0);
  const root = useRef(null);
  const field = useRef(null);
  useScrollEdge(field); // a long draft holds the page while scrolling in it, a short one does not
  const picker = useRef(null);
  const meter = useRef(null);
  const recordingRef = useRef(null);
  // The thumbnails as of the last change, ahead of `images` within one event (two
  // pastes in a row), for the count limit and to free them on unmount.
  const imagesRef = useRef([]);
  const captureRef = useRef(null);
  captureRef.current = capture;

  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text, recording]);

  useEffect(() => {
    const el = field.current;
    if (!(autoFocus || focusTick) || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [autoFocus, focusTick]);

  // Gone (another request, a practice switch): the previews and the microphone are freed.
  useEffect(() => () => {
    imagesRef.current.forEach((b) => URL.revokeObjectURL(b.preview));
    if (captureRef.current) URL.revokeObjectURL(captureRef.current);
    const rec = recordingRef.current;
    if (rec) { recordingRef.current = null; rec.discarded = true; releaseRecording(rec); }
  }, []);

  const addImages = useCallback((files) => {
    if (!imagesOn || !files.length) return;
    const fitting = files.filter(isImage);
    const free = Math.max(0, maxImages - imagesRef.current.length);
    const added = fitting.slice(0, free);
    if (fitting.length < files.length) setNotice(UPLOAD_TEXT.unsupported);
    else if (added.length < fitting.length) setNotice(`Höchstens ${maxImages} Bilder je Nachricht.`);
    else setNotice('');
    if (!added.length) return;
    const thumbs = added.map((file) => ({ key: thumbKey(), preview: URL.createObjectURL(file), status: 'uploading' }));
    imagesRef.current = [...imagesRef.current, ...thumbs];
    setImages((old) => [...old, ...thumbs]);
    thumbs.forEach((thumb, i) => {
      const update = (patch) => setImages((old) => old.map((b) => (b.key === thumb.key ? { ...b, ...patch } : b)));
      uploadImage(added[i]).then(
        (attachment) => update({ status: 'done', attachment }),
        (err) => {
          if (!imagesRef.current.some((b) => b.key === thumb.key)) return; // removed meanwhile
          const reason = (err && err.message) || UPLOAD_TEXT.failed;
          update({ status: 'failed', reason });
          setNotice(reason);
        },
      );
    });
  }, [imagesOn, maxImages, uploadImage]);

  // Dropping works anywhere on the page while the composer is there; otherwise
  // the browser would open a dropped file as a page of its own. No dragleave (it
  // flickers on every child element): the highlight goes out 200 ms after the
  // last dragover. A drop zone of the page itself (it prevents the default)
  // keeps its drop.
  useEffect(() => {
    if (!imagesOn) return undefined;
    let timer = null;
    const withFiles = (e) => !e.defaultPrevented && Array.from(e.dataTransfer?.types || []).includes('Files');
    const over = (e) => {
      if (!withFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDragging(true);
      clearTimeout(timer);
      timer = setTimeout(() => setDragging(false), 200);
    };
    const drop = (e) => {
      if (!withFiles(e)) return;
      e.preventDefault();
      clearTimeout(timer);
      setDragging(false);
      addImages(Array.from(e.dataTransfer.files || []));
    };
    document.addEventListener('dragover', over);
    document.addEventListener('drop', drop);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('dragover', over);
      document.removeEventListener('drop', drop);
    };
  }, [imagesOn, addImages]);

  const pasteImages = (e) => {
    const clipboard = e.clipboardData;
    if (!clipboard) return;
    // Text wins: Word and Excel put a picture of the copied text on the clipboard as well.
    if (Array.from(clipboard.types || []).includes('text/plain')) return;
    const fromItems = Array.from(clipboard.items || [])
      .filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    const files = fromItems.length ? fromItems : Array.from(clipboard.files || []);
    if (!files.length) return;
    e.preventDefault();
    addImages(files);
  };

  const takeScreenshot = async () => {
    try {
      const blob = await captureTab(root.current ? root.current.closest('.sw-panel') : null);
      if (blob) setCapture(URL.createObjectURL(blob));
      else setNotice(SCREENSHOT_FAILED);
    } catch (err) {
      // Cancelled is no error: that was on purpose.
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') setNotice(SCREENSHOT_FAILED);
    }
  };

  /** Takes thumbnails out of the strip and frees their previews. */
  const dropImages = (keys) => {
    const gone = new Set(keys);
    imagesRef.current.forEach((b) => { if (gone.has(b.key)) URL.revokeObjectURL(b.preview); });
    imagesRef.current = imagesRef.current.filter((b) => !gone.has(b.key));
    setImages((old) => old.filter((b) => !gone.has(b.key)));
  };
  const removeImage = (key) => { dropImages([key]); setNotice(''); };

  const stopRecording = (discard) => {
    const rec = recordingRef.current;
    if (!rec) return;
    recordingRef.current = null;
    rec.discarded = discard;
    setRecording(false);
    if (rec.recorder.state !== 'inactive') rec.recorder.stop(); else releaseRecording(rec);
    if (discard) setFocusTick((n) => n + 1);
  };

  const startRecording = async () => {
    if (!dictationOn || recordingRef.current || converting) return;
    setNotice('');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (err) {
      setNotice(err?.name === 'NotAllowedError' || err?.name === 'SecurityError'
        ? 'Das Mikrofon ist für diese Seite nicht freigegeben. Erlauben Sie es über das Symbol links in der Adresszeile.'
        : 'Es wurde kein Mikrofon gefunden.');
      return;
    }
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
      .find((t) => window.MediaRecorder.isTypeSupported?.(t));
    let recorder;
    try {
      recorder = new window.MediaRecorder(stream, type ? { mimeType: type } : undefined);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      setNotice('Dieser Browser kann nicht aufnehmen.');
      return;
    }
    const rec = {
      recorder, stream, chunks: [], start: Date.now(), timer: null, frame: null, audio: null,
      discarded: false, peak: 0, measured: 0,
    };
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) rec.chunks.push(e.data); };
    recorder.onstop = async () => {
      releaseRecording(rec);
      if (recordingRef.current === rec) { recordingRef.current = null; setRecording(false); }
      if (rec.discarded) return;
      if (Date.now() - rec.start < 1000 || !rec.chunks.length) {
        setNotice('Zu kurz. Halten Sie die Aufnahme etwas länger.');
      } else if (rec.measured > 10 && rec.peak < 0.05) {
        // Silence does not go out: from a silent recording the model invented
        // believable sentences, which would then stand in the field as if spoken.
        // Checked only when the level was really measured; without a running
        // AudioContext every recording would look silent.
        setNotice(DICTATION_TEXT.noSpeech);
      } else {
        setConverting(true);
        const result = await dictate(new Blob(rec.chunks, { type: recorder.mimeType || type || 'audio/webm' }));
        setConverting(false);
        if (result.ok) setText((t) => (t.trim() ? `${t.trimEnd()} ` : '') + result.text);
        else setNotice(result.error);
      }
      setFocusTick((n) => n + 1);
    };
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        rec.audio = new AudioContextClass();
        if (rec.audio.state === 'suspended') rec.audio.resume().catch(() => {});
        const analyser = rec.audio.createAnalyser();
        analyser.fftSize = 1024;
        rec.audio.createMediaStreamSource(stream).connect(analyser);
        const samples = new Uint8Array(analyser.fftSize);
        const draw = () => {
          analyser.getByteTimeDomainData(samples);
          let sum = 0;
          for (let i = 0; i < samples.length; i += 1) { const v = (samples[i] - 128) / 128; sum += v * v; }
          const level = Math.min(1, Math.max(0, Math.sqrt(Math.sqrt(sum / samples.length)) * 1.8 - 0.15));
          if (meter.current) meter.current.style.setProperty('--pegel', level.toFixed(2));
          if (rec.audio.state === 'running') { rec.measured += 1; if (level > rec.peak) rec.peak = level; }
          rec.frame = requestAnimationFrame(draw);
        };
        rec.frame = requestAnimationFrame(draw);
      }
    } catch { /* it works without the level meter */ }
    rec.timer = setInterval(() => {
      const s = Math.floor((Date.now() - rec.start) / 1000);
      setSeconds(s);
      if (s >= maxSeconds) stopRecording(false);
    }, 250);
    recordingRef.current = rec;
    recorder.start(1000);
    setSeconds(0);
    setRecording(true);
  };

  const done = images.filter((b) => b.status === 'done');
  const uploading = images.some((b) => b.status === 'uploading');
  const canSend = !sending && !uploading && !recording && !converting && (!!text.trim() || done.length > 0);

  const send = async () => {
    if (!canSend) return;
    const sent = images;
    const failed = sent.filter((b) => b.status === 'failed').length;
    const result = await onSend(text.trim(), done.map((b) => b.attachment.id));
    if (!result || !result.ok) {
      setNotice((result && result.error) || SUPPORT_TEXT.notSent);
      return;
    }
    setText('');
    // Everything that stood there when sending is done with, the failed ones too
    // (they did not go out, and the notice says so); an image added meanwhile stays.
    dropImages(sent.map((b) => b.key));
    if (failed === 0) setNotice('');
    else if (failed === 1) setNotice('Ein Bild konnte nicht angehängt werden und ist nicht mitgegangen.');
    else setNotice(`${failed} Bilder konnten nicht angehängt werden und sind nicht mitgegangen.`);
  };

  return (
    <div className="sw-eingabe" ref={root}>
      {images.length > 0 && (
        <div className="sw-leiste" aria-label="Angehängte Bilder">
          {images.map((b) => (
            <div key={b.key} className={`sw-daumen ${THUMB_STATE[b.status]}`} title={b.reason || undefined}>
              <img src={b.preview} alt="" />
              {b.status === 'uploading' && <span className="sw-lader" role="img" aria-label="Wird hochgeladen" />}
              {b.status === 'failed' && <span className="sw-fehlerzeichen" aria-hidden="true">!</span>}
              {b.status === 'done' && (
                <button type="button" className="sw-malknopf" onClick={() => setEditing({ key: b.key, url: b.preview })}
                  aria-label="Bild bearbeiten" title="Ausschneiden, zeichnen, Pfeil setzen">
                  <Pencil /> Bearbeiten
                </button>
              )}
              <button type="button" className="sw-weg" onClick={() => removeImage(b.key)} aria-label="Bild entfernen">
                <Close size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className={`sw-feld${dragging ? ' ist-ziehen' : ''}${recording ? ' ist-aufnahme' : ''}`}>
        {recording ? (
          <>
            <button type="button" className="sw-rund" onClick={() => stopRecording(true)} aria-label="Aufnahme verwerfen" title="Verwerfen">
              <Close />
            </button>
            <div className="sw-aufnahme">
              <span className="sw-laeuft" aria-hidden="true" />
              <span className="sw-zeit" aria-hidden="true">{mmss(seconds)}</span>
              <span className="sw-pegel" ref={meter} aria-hidden="true">
                {[0.5, 0.8, 1, 0.7, 0.45].map((f) => <i key={f} style={{ '--f': f }} />)}
              </span>
              <span className="sw-aufnahme-text" role="status">Wird als Text eingefügt</span>
            </div>
            <button type="button" className="sw-senden" onClick={() => stopRecording(false)}
              aria-label="Aufnahme beenden und in Text umwandeln" title="Fertig">
              <Check />
            </button>
          </>
        ) : (
          <>
            {imagesOn && (canCaptureScreen() ? (
              <button type="button" className="sw-rund sw-foto" onClick={takeScreenshot} disabled={images.length >= maxImages}
                aria-label="Bildschirmfoto aufnehmen" title={`Bildschirmfoto aufnehmen. Bilder auch mit ${PASTE_KEYS} oder Hineinziehen`}>
                <ScreenCapture />
              </button>
            ) : (
              <>
                <button type="button" className="sw-rund" onClick={() => picker.current?.click()} disabled={images.length >= maxImages}
                  aria-label="Bild anhängen" title={`Bild anhängen. Auch mit ${PASTE_KEYS} oder Hineinziehen`}>
                  <Plus />
                </button>
                <input ref={picker} type="file" hidden multiple accept={IMAGE_TYPES}
                  onChange={(e) => { addImages(Array.from(e.target.files || [])); e.target.value = ''; }} />
              </>
            ))}
            <textarea
              ref={field}
              rows={1}
              value={text}
              maxLength={MAX_MESSAGE_LENGTH}
              aria-label="Nachricht an das DeskBot-Team"
              placeholder={placeholder}
              // Read-only while sending, so nothing typed meanwhile is cleared with the sent text.
              readOnly={sending}
              // Off while a recording turns into text, which then lands at the end of the draft.
              disabled={converting}
              onChange={(e) => setText(e.target.value)}
              onPaste={imagesOn ? pasteImages : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
              }}
            />
            {dictationOn && (converting ? (
              <span className="sw-rund warte" role="img" aria-label="Wird in Text umgewandelt"><span className="sw-lader" aria-hidden="true" /></span>
            ) : (
              <button type="button" className="sw-rund" onClick={startRecording} aria-label="Diktieren" title="Diktieren. Wird als Text eingefügt">
                <Mic />
              </button>
            ))}
            <button type="button" className="sw-senden" onClick={send} disabled={!canSend} aria-label="Senden"
              title={uploading ? 'Bilder werden noch hochgeladen' : undefined}>
              <ArrowUp />
            </button>
          </>
        )}
      </div>
      {capture && (
        <ImageEditor url={capture}
          onCancel={() => { URL.revokeObjectURL(capture); setCapture(null); }}
          onDone={(file) => { URL.revokeObjectURL(capture); setCapture(null); addImages([file]); }} />
      )}
      {editing && (
        <ImageEditor url={editing.url} onCancel={() => setEditing(null)}
          onDone={(file) => { const { key } = editing; setEditing(null); removeImage(key); addImages([file]); }} />
      )}
      {notice && <div className="sw-meldung" role="alert">{notice}</div>}
      {!notice && converting && <div className="sw-hinweis" role="status">Wird in Text umgewandelt …</div>}
      {!notice && !converting && dragging && <div className="sw-hinweis">Loslassen, um das Bild anzuhängen</div>}
    </div>
  );
};

/* ── Images in the thread ──────────────────────────────────────────────── */

/** Fetched through the provider (with the token), not by <img src>: the endpoint checks the login. */
const AttachmentImage = ({ attachment, onOpen, wide, style }) => {
  const support = useSupport();
  const imageUrl = support?.imageUrl;
  const [url, setUrl] = useState(() => (support ? support.cachedImageUrl(attachment.id) : null));
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    if (url || !imageUrl) return undefined;
    let cancelled = false;
    imageUrl(attachment.id).then(
      (loaded) => { if (!cancelled) setUrl(loaded); },
      () => { if (!cancelled) setBroken(true); },
    );
    return () => { cancelled = true; };
  }, [attachment.id, url, imageUrl]);
  const className = `sw-bild${wide ? ' breit' : ''}`;
  if (broken) return <div className={`${className} kaputt`} style={style}>Bild nicht verfügbar</div>;
  return (
    <button type="button" className={className} style={style} onClick={() => url && onOpen(url)} disabled={!url}
      aria-label="Bild vergrößern">
      {url ? <img src={url} alt="" /> : <span className="sw-lader" aria-hidden="true" />}
    </button>
  );
};

/** The shape of a single image before it has loaded. */
const singleShape = (a) => {
  const raw = a.width && a.height ? a.width / a.height : 4 / 3;
  // Portrait down to 9:16: phone screenshots are mostly portrait, and at 3:4 a
  // strip was missing top and bottom. Landscape up to 32:9: wide monitors are the
  // norm, and a cropped screenshot is worthless.
  const ratio = Math.min(3.6, Math.max(0.5625, raw));
  return { tall: ratio < 1, extraWide: ratio >= 1.9, ratio: String(ratio) };
};

/**
 * The aspect ratios are fixed BEFORE an image has loaded, otherwise the thread
 * jumps. They sit on the image, not on the frame, so the name fits above without
 * squeezing it; the width comes from the CSS (portrait or landscape) so it can
 * grow in a wide window. `inner`: the images stand in a bubble with text, whose
 * width rules.
 */
const ImageGrid = ({ attachments, onOpen, meta = null, title, head = null, first = false, inner = false }) => {
  const single = attachments.length === 1;
  const shape = single ? singleShape(attachments[0]) : null;
  const form = single
    ? (inner ? ' einzeln' : ` einzeln ${shape.tall ? 'hoch' : 'quer'}${shape.extraWide ? ' breitformat' : ''}`)
    : '';
  return (
    <div className={`sw-bilder${form}${inner ? ' innen' : ''}${first ? ' erste' : ''}`} title={title}>
      {head}
      {attachments.map((a, i) => (
        <AttachmentImage key={a.id} attachment={a} onOpen={onOpen}
          wide={!single && attachments.length % 2 === 1 && i === attachments.length - 1}
          style={shape ? { aspectRatio: shape.ratio } : undefined} />
      ))}
      {meta}
    </div>
  );
};

/**
 * The large view, fitted to the screen. A click on the image enlarges it at the
 * clicked spot (to the real size, at least twice), where it can then be moved;
 * a second click fits it again. On a screenshot the text must become readable.
 */
const Lightbox = ({ url, onClose }) => {
  const closeButton = useRef(null);
  const stage = useRef(null);
  const image = useRef(null);
  const [zoom, setZoom] = useState(null); // null = fitted, otherwise {width, fx, fy}
  const [natural, setNatural] = useState(0); // the real width: limits how far fitting may enlarge
  // After zooming in, the clicked spot stands in the middle, not the image's top left corner.
  useLayoutEffect(() => {
    const s = stage.current;
    const img = image.current;
    if (!zoom || !s || !img) return;
    s.scrollLeft = Math.max(0, img.offsetWidth * zoom.fx - s.clientWidth / 2);
    s.scrollTop = Math.max(0, img.offsetHeight * zoom.fy - s.clientHeight / 2);
  }, [zoom]);
  const toggle = (e) => {
    e.stopPropagation();
    const img = image.current;
    if (!img) return;
    if (zoom) { setZoom(null); return; }
    const r = img.getBoundingClientRect();
    const width = Math.min(Math.max(img.naturalWidth, r.width * 2), r.width * 4);
    if (width <= r.width + 1) return;
    setZoom({ width, fx: (e.clientX - r.left) / r.width, fy: (e.clientY - r.top) / r.height });
  };
  useEffect(() => {
    const before = document.activeElement;
    closeButton.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === 'Tab') { e.preventDefault(); closeButton.current?.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (before && typeof before.focus === 'function') before.focus();
    };
  }, [onClose]);
  return createPortal(
    <div className={`sw-leuchte${zoom ? ' gezoomt' : ''}`} role="dialog" aria-modal="true" aria-label="Bild"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sw-buehne" ref={stage} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <img ref={image} src={url} alt="Angehängtes Bild" onClick={toggle}
          onLoad={(e) => setNatural(e.currentTarget.naturalWidth || 0)}
          style={zoom ? { width: `${Math.round(zoom.width)}px` } : (natural ? { '--sw-max': `${natural * 4}px` } : undefined)}
          title={zoom ? 'Klicken zum Einpassen' : 'Klicken zum Vergrößern'} />
      </div>
      <button ref={closeButton} type="button" className="sw-leuchte-zu" onClick={onClose} aria-label="Schließen">
        <Close size={18} />
      </button>
    </div>,
    document.body,
  );
};

/* ── Thread ────────────────────────────────────────────────────────────── */

/**
 * Consecutive messages of the same side and the same person (on the practice
 * side: you, or one colleague) within 10 minutes form a group. An event (the
 * team took the request on) stands on its own between them.
 */
const groupMessages = (messages) => {
  const out = [];
  let lastDay = null;
  for (const m of messages) {
    const day = dayKey(new Date(m.at));
    if (day !== lastDay) { out.push({ kind: 'day', at: m.at, key: `t-${m.id}` }); lastDay = day; }
    if (m.side === 'system') { out.push({ kind: 'event', message: m, key: `e-${m.id}` }); continue; }
    // '' for your own messages: they carry no name.
    const who = m.side === 'team' || !m.mine ? (m.from?.name || '') : '';
    const previous = out[out.length - 1];
    const same = previous && previous.kind === 'group' && previous.side === m.side && previous.who === who
      && new Date(m.at) - new Date(previous.messages[previous.messages.length - 1].at) < 10 * 60 * 1000;
    if (same) previous.messages.push(m);
    else out.push({ kind: 'group', side: m.side, who, messages: [m], key: `g-${m.id}` });
  }
  return out;
};

/**
 * Tooltip of the read ticks: who read it and when, i.e. the time of the read
 * marker (readAt), not the time of the message it reached (at, which only
 * decides which ticks are blue). Without readAt it names the reader only.
 */
const readTitle = (teamRead) => {
  const who = `Gelesen von ${teamRead.from?.name || 'DeskBot'}`;
  return teamRead.readAt ? `${who} · ${clockTime(teamRead.readAt)}` : who;
};

/**
 * The team took the request on: a note rather than a message, framed in blue
 * with the face of whoever took it and the time. The word "Ticket" stays internal.
 */
const TicketEvent = ({ message }) => (
  <div className="sw-ereignis" role="note">
    {message.from?.name ? <Face name={message.from.name} /> : <CheckCircle />}
    <span className="sw-ereignis-text">{message.text}</span>
    <time dateTime={message.at}>{clockTime(message.at)}</time>
  </div>
);

const Thread = ({ conversation, showTicks }) => {
  const stream = useRef(null);
  useScrollEdge(stream);
  const [lightbox, setLightbox] = useState(null); // object URL of the image shown large
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const parts = useMemo(() => groupMessages(conversation.messages || []), [conversation.messages]);
  const teamRead = conversation.teamRead || null;
  const isRead = (m) => !!teamRead && teamRead.at >= m.at;
  const count = conversation.messages ? conversation.messages.length : 0;

  useEffect(() => {
    const el = stream.current;
    if (el) el.scrollTop = el.scrollHeight;
    markEdge(el);
  }, [conversation.id, count]);

  return (
    <div className="sw-strom" ref={stream} onScroll={(e) => markEdge(e.currentTarget)} role="log" aria-live="polite" aria-label="Verlauf">
      {parts.map((part) => {
        if (part.kind === 'day') return <div key={part.key} className="sw-pill">{dayLabel(part.at)}</div>;
        if (part.kind === 'event') return <TicketEvent key={part.key} message={part.message} />;
        const own = part.side === 'practice';
        return (
          <div key={part.key} className={`sw-gruppe ${own ? 'eigen' : 'team'}`}>
            {part.messages.map((m, i) => {
              const read = isRead(m);
              const first = i === 0;
              const images = m.attachments || [];
              const meta = (
                <span className="sw-meta">
                  {m.editedAt && <span className="sw-bearbeitet" title={`Bearbeitet am ${fullTime(m.editedAt)}`}>Bearbeitet</span>}
                  <time dateTime={m.at}>{clockTime(m.at)}</time>
                  {own && showTicks && (
                    <ReadTicks read={read} title={read ? readTitle(teamRead) : 'Zugestellt'} />
                  )}
                </span>
              );
              // The name belongs at the start of the group: above the image when the first message begins with one.
              // The team's always; on the practice side a colleague's (part.who), never your own.
              let name = null;
              if (first && !own) {
                name = <span className="sw-name" style={{ color: nameTextColor(part.who) }}>{part.who || 'DeskBot-Team'}</span>;
              } else if (first && part.who) {
                name = <span className="sw-name">{part.who}</span>;
              }
              if (images.length && m.text) {
                // Image WITH text: one bubble, the images on top and the text flush below, the same width.
                const shape = images.length === 1 ? singleShape(images[0]) : null;
                const form = `${shape && shape.tall ? ' hoch' : ''}${shape && shape.extraWide ? ' breitformat' : ''}`;
                return (
                  <div key={m.id} className={`sw-blase mit-bildern${form}${first ? ' erste' : ''}${m.editedAt ? ' bearbeitet' : ''}`} title={fullTime(m.at)}>
                    {name}
                    <ImageGrid attachments={images} onOpen={setLightbox} inner />
                    <span className="sw-unterschrift">
                      <span className="sw-text">{m.text}</span>
                      <span className="sw-platz" aria-hidden="true" />
                      {meta}
                    </span>
                  </div>
                );
              }
              if (images.length) {
                return (
                  <ImageGrid key={m.id} attachments={images} onOpen={setLightbox} title={fullTime(m.at)}
                    meta={meta} head={name} first={first} />
                );
              }
              if (!m.text) return null;
              return (
                <div key={m.id} className={`sw-blase${first ? ' erste' : ''}${m.editedAt ? ' bearbeitet' : ''}`} title={fullTime(m.at)}>
                  {name}
                  <span className="sw-text">{m.text}</span>
                  <span className="sw-platz" aria-hidden="true" />
                  {meta}
                </div>
              );
            })}
          </div>
        );
      })}
      {lightbox && <Lightbox url={lightbox} onClose={closeLightbox} />}
    </div>
  );
};

/* ── Views ─────────────────────────────────────────────────────────────── */

/** Who from the team wrote last: from the whole thread, otherwise from the list entry. */
const replyFrom = (c) => {
  if (!c) return null;
  if (Array.isArray(c.messages)) {
    const m = [...c.messages].reverse().find((x) => x.side === 'team');
    return m?.from?.name || null;
  }
  if (c.last?.side === 'team') return c.last.from?.name || null;
  return null;
};

/** A small face from the team: the photo, otherwise the initials in the person's colour. */
const Face = ({ name }) => {
  const [broken, setBroken] = useState(false);
  const photo = teamPhoto(name);
  if (photo && !broken) return <img className="sw-gesicht" src={photo} alt="" onError={() => setBroken(true)} />;
  const initials = String(name || '?').trim().split(/\s+/).map((t) => t[0]).join('').slice(0, 2).toUpperCase();
  return <span className="sw-gesicht" style={{ background: nameColor(name) }} aria-hidden="true">{initials}</span>;
};

/** The channel's mark (DeskBot) with, as a crescent, whoever answers. */
const Channel = ({ who }) => (
  <span className="sw-kanal" aria-hidden="true">
    <span className="sw-marke"><Bubble size={16} /></span>
    {who && <Face name={who} />}
  </span>
);

const Header = ({ support, title, subtitle, actions, fullTitle, who = null }) => (
  <header className="sw-kopf">
    <Channel who={who} />
    <div className="sw-kopf-text">
      <h2 title={fullTitle}>{title}</h2>
      {subtitle && <p title={subtitle}>{subtitle}</p>}
    </div>
    {actions}
    <button type="button" className="sw-icon" onClick={support.collapse} aria-label="Einklappen" title="Einklappen"><Collapse /></button>
  </header>
);

const NewButton = ({ onClick }) => (
  <button type="button" className="sw-neu" onClick={onClick}>
    <Pencil /> Neue Anfrage
  </button>
);

/**
 * What the request refers to, as data rather than a sentence: visible to the
 * practice (so it knows what goes along) and not editable (so the team can rely
 * on it). The task id is not shown; it travels with the request for the team.
 */
const ReferenceCard = ({ reference, underHeader = false, practiceId }) => {
  if (!reference) return null;
  const who = [reference.callerName, reference.phone].map((x) => String(x || '').trim()).filter(Boolean);
  // Under the header of a request: back to the call itself, the Tasks page filtered to it
  // with the task highlighted (the team's deep link, inside the app). The window stays open.
  const href = underHeader && practiceId && reference.kind === 'task'
    ? taskLinkPath({ practiceId, sessionId: reference.sessionId, taskId: reference.id })
    : null;
  return (
    <div className={`sw-bezug${underHeader ? ' unter-kopf' : ''}`}>
      <span className="was">{reference.title || 'Zu einem Vorgang'}</span>
      {who.length > 0 && <span className="wer">{who.join(' · ')}</span>}
      {href && <Link className="auf" to={href}>Anruf anzeigen</Link>}
    </div>
  );
};

/** Under the question on the start, only where images can be sent: how to show instead of describe. */
const ImageHint = () => (
  <p className="sw-bildhinweis">
    {canCaptureScreen()
      ? <>Zeigen ist leichter als beschreiben: unten auf das <b>Fotosymbol</b> tippen, im Browser <b>„Teilen"</b> bestätigen und den <b>Bereich auswählen</b>, um den es geht. Dann verstehen wir es sofort.</>
      : <>Ein Bild sagt oft mehr als eine Beschreibung. Mit {PASTE_KEYS} einfügen oder ins Fenster ziehen.</>}
  </p>
);

/**
 * The start: write at once. The field is there with the cursor in it; sending
 * opens a new request and lands in its conversation.
 */
const Start = ({ support }) => {
  const send = async (text, attachmentIds) => {
    const result = await support.startConversation(text, attachmentIds);
    if (result.ok) support.showWindow({ conversation: result.id });
    return result;
  };
  const withReply = support.conversations.find((c) => c.unread > 0) || null;
  const who = withReply?.last?.side === 'team' ? withReply.last.from?.name : null;
  // A request the team opened ("Praxis anschreiben") was never asked by the practice: the team wrote first.
  const wrote = withReply?.openedByTeam ? 'hat Ihnen geschrieben' : 'hat geantwortet';
  return (
    <div className="sw-ansicht">
      <Header support={support} title="Support" subtitle={withPractice(support, 'DeskBot-Team')} />
      {support.error && <div className="sw-hinweis-oben" role="status">{support.error}</div>}
      <div className="sw-start">
        <span className="symbol" aria-hidden="true"><Bubble size={24} /></span>
        <h3>Wie können wir helfen?</h3>
        {support.features.images && <ImageHint />}
        {withReply && (
          <button type="button" className="sw-antwortkarte" onClick={() => support.showWindow({ conversation: withReply.id })}>
            <Channel who={replyFrom(withReply)} />
            <span className="text">
              <span className="wer">{who ? `${who} ${wrote}` : 'Neues zu Ihrer Anfrage'}</span>
              <span className="worum">{withReply.title}</span>
            </span>
            <span className="sw-zahl" aria-label={`${withReply.unread} neue ${withReply.unread === 1 ? 'Nachricht' : 'Nachrichten'}`}>
              {withReply.unread}
            </span>
          </button>
        )}
      </div>
      <ReferenceCard reference={support.reference} />
      <Composer onSend={send} sending={support.sending} autoFocus placeholder="Nachricht an das DeskBot-Team" />
    </div>
  );
};

/**
 * The requests stay on the left, permanently, not in a drawer: a drawer behind
 * an icon is never found by users who do not know that icon. On narrow screens
 * the list moves above the conversation instead of disappearing.
 */
const RequestList = ({ support }) => {
  const { conversations } = support;
  const list = useRef(null);
  useScrollEdge(list);
  return (
    <nav className="sw-spalte" aria-label="Anfragen">
      <div className="sw-spalte-kopf">
        <h2>Anfragen</h2>
      </div>
      <div className="sw-liste" ref={list} onScroll={(e) => markEdge(e.currentTarget)}>
        {!support.loaded && <div className="sw-liste-leer"><span className="sw-lader" aria-label="Lädt" /></div>}
        {support.loaded && conversations.length === 0 && <div className="sw-liste-leer">Noch keine Anfragen.</div>}
        {conversations.map((c) => {
          const hasNews = c.unread > 0;
          return (
            <button
              key={c.id}
              type="button"
              className={`sw-zeile${hasNews ? ' ist-neu' : ''}${c.closed ? ' ist-erledigt' : ''}`}
              aria-current={support.view === 'thread' && c.id === support.currentId ? 'true' : undefined}
              onClick={() => support.showWindow({ conversation: c.id })}
            >
              <span className="z1">
                <span className="titel" title={c.title}>{c.title}</span>
                <time dateTime={c.lastActivity || undefined} title={fullTime(c.lastActivity)}>{listTime(c.lastActivity)}</time>
              </span>
              <span className="z2">
                <span className="vorschau">{previewLine(c.last)}</span>
                {hasNews && <span className="sw-badge" aria-label={`${c.unread} neu`}>{c.unread}</span>}
                {!hasNews && c.closed && <span className="sw-erledigt">Erledigt</span>}
              </span>
            </button>
          );
        })}
      </div>
      <div className="sw-spalte-fuss">
        <NewButton onClick={() => support.showWindow({ isNew: true })} />
      </div>
    </nav>
  );
};

const Conversation = ({ support }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { current, markRead, windowOpen } = support;
  // The list entry paints the header while the messages are still loading.
  const entry = support.conversations.find((c) => c.id === support.currentId) || null;
  const head = current || entry;
  const who = replyFrom(head);

  // Report read only while the window is open, this request is on screen AND the
  // browser tab is in front and focused: a tab in the background has read nothing.
  const unread = entry?.unread || 0;
  useEffect(() => {
    if (!current || !unread || !markRead || !windowOpen) return undefined;
    const report = () => { if (document.visibilityState === 'visible' && document.hasFocus()) markRead(current.id); };
    report();
    document.addEventListener('visibilitychange', report);
    window.addEventListener('focus', report);
    return () => { document.removeEventListener('visibilitychange', report); window.removeEventListener('focus', report); };
  }, [current, unread, markRead, windowOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [menuOpen]);

  let subtitle = who ? `${who} · DeskBot-Team` : 'DeskBot-Team';
  if (head?.closed) subtitle = 'Erledigt';

  return (
    <div className="sw-ansicht">
      <Header
        support={support}
        title={head?.title || 'Anfrage'}
        fullTitle={head?.title}
        who={who}
        subtitle={withPractice(support, subtitle)}
        actions={head ? (
          <span ref={menuRef}>
            <button type="button" className="sw-icon" onClick={() => setMenuOpen((open) => !open)} aria-haspopup="menu"
              aria-expanded={menuOpen} aria-label="Mehr" title="Mehr"><More /></button>
            {menuOpen && (
              <div className="sw-menue" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); support.setClosed(head.id, !head.closed); }}>
                  <Check /> {head.closed ? 'Wieder öffnen' : 'Als erledigt markieren'}
                </button>
              </div>
            )}
          </span>
        ) : null}
      />
      {/* Right under the header: what this request is about. Once sent, the card
          comes from the stored request, no longer from the window's state. */}
      <ReferenceCard reference={head?.reference} underHeader practiceId={support.practiceId} />
      {current ? <Thread conversation={current} showTicks={support.features.readReceipts} /> : (
        <div className="sw-leer">
          {support.currentError ? (
            <div className="sw-leer-fehler" role="alert">
              <p>{support.currentError}</p>
              <button type="button" onClick={support.reload}>Erneut versuchen</button>
            </div>
          ) : <span className="sw-lader" aria-label="Lädt" />}
        </div>
      )}
      {/* key: a sentence begun (and images attached) belongs to ITS request, not to the next one. */}
      <Composer key={support.currentId || 'lade'} onSend={support.sendReply} sending={support.sending}
        placeholder={head?.closed ? 'Schreiben öffnet die Anfrage wieder' : 'Nachricht'} />
    </div>
  );
};

/**
 * Snap to whole device pixels. At fractional display scaling (125 % and 150 %
 * are common on Windows) a whole CSS position lands between device pixels and
 * the browser draws the whole window blurred. A sub-pixel nudge of right/bottom
 * puts every edge on a whole pixel again; at 100 % and 200 % nothing changes.
 */
const useWholePixels = (ref) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const snap = () => {
      el.style.right = '';
      el.style.bottom = '';
      const dpr = window.devicePixelRatio || 1;
      if (Math.abs(dpr - Math.round(dpr)) < 0.001) return;
      const box = el.getBoundingClientRect();
      const style = getComputedStyle(el);
      const restX = ((box.left * dpr) % 1 + 1) % 1;
      const restY = ((box.top * dpr) % 1 + 1) % 1;
      if (restX > 0.01) el.style.right = `${parseFloat(style.right) + restX / dpr}px`;
      if (restY > 0.01) el.style.bottom = `${parseFloat(style.bottom) + restY / dpr}px`;
    };
    snap();
    window.addEventListener('resize', snap);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(snap) : null;
    if (observer) observer.observe(el);
    // Zooming fires resize; moving to a monitor with another scaling does not reliably, hence this listener.
    let resolution = null;
    try {
      resolution = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      resolution.addEventListener('change', snap);
    } catch { resolution = null; }
    return () => {
      window.removeEventListener('resize', snap);
      if (observer) observer.disconnect();
      if (resolution) resolution.removeEventListener('change', snap);
    };
  });
};

/* ── The window ────────────────────────────────────────────────────────── */

const SupportWindow = () => {
  const support = useSupport();
  const showWindow = support?.showWindow;
  const practiceId = support?.practiceId || null;
  const loaded = !!support?.loaded;
  const panelRef = useRef(null);
  useWholePixels(panelRef);
  // The list reaches the decision below through a ref, so it is always the
  // latest one without re-running the decision with every incoming message.
  const conversationsRef = useRef([]);
  conversationsRef.current = support?.conversations || [];
  const [toast, setToast] = useState('');
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(''), 7000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Room at the bottom of the page for the starter, only while the chat exists here.
  useEffect(() => {
    if (!practiceId) return undefined;
    document.body.classList.add('support-chat-on');
    return () => document.body.classList.remove('support-chat-on');
  }, [practiceId]);

  // Opened from a task row. The window only ever writes to the practice on
  // screen, so an event for another practice is ignored.
  const [pendingOpen, setPendingOpen] = useState(null);
  useEffect(() => {
    if (!practiceId) return undefined;
    const onOpen = (e) => {
      const detail = (e && e.detail) || {};
      if (!detail.practiceId || detail.practiceId !== practiceId) return;
      setPendingOpen({ reference: referenceFromOpenEvent(detail) });
    };
    window.addEventListener('deskbot-support-open', onOpen);
    return () => window.removeEventListener('deskbot-support-open', onOpen);
  }, [practiceId]);

  // Decided once the list is there. One call, one conversation: tapping the same
  // task twice rarely means two concerns, usually the first request just went
  // out of sight, and two requests about one call are double work for the team
  // and two places for the practice to look for the answer. Erledigt requests do
  // not count: what is closed may be opened anew.
  useEffect(() => {
    if (!pendingOpen || !loaded || !showWindow) return;
    setPendingOpen(null);
    const { reference } = pendingOpen;
    const running = reference
      ? conversationsRef.current.find((c) => !c.closed && c.reference && c.reference.kind === 'task' && c.reference.id === reference.id)
      : null;
    if (running) {
      setToast('Zu diesem Anruf läuft schon eine Anfrage. Hier ist sie.');
      showWindow({ conversation: running.id });
      return;
    }
    showWindow({ isNew: true, reference });
  }, [pendingOpen, loaded, showWindow]);

  if (!support || !support.practiceId) return null;

  if (!support.windowOpen) {
    const n = support.unreadCount;
    // With an answer waiting, the window opens right at it: that is what the click was for.
    const withReply = support.conversations.find((c) => c.unread > 0) || null;
    const who = withReply ? replyFrom(withReply) : null;
    const open = () => support.showWindow(withReply ? { conversation: withReply.id } : {});
    return (
      <button
        type="button"
        className="sw-starter"
        onClick={open}
        aria-label={n ? `Support: ${n} Anfrage${n === 1 ? '' : 'n'} mit Neuem${who ? `, zuletzt von ${who}` : ''}` : 'Support: dem DeskBot-Team schreiben'}
      >
        <span className="sw-wolke" aria-hidden="true">DeskBot-Team schreiben</span>
        <Bubble size={25} />
        {n > 0 && who && <Face name={who} />}
        {n > 0 && <span className="sw-zahl" aria-hidden="true">{n > 99 ? '99+' : n}</span>}
      </button>
    );
  }

  return (
    <section ref={panelRef} className="sw-panel" aria-label="Support" data-view={support.view}>
      {toast && <div className="sw-toast" role="status">{toast}</div>}
      <RequestList support={support} />
      <div className="sw-haupt">
        {support.view === 'thread' ? <Conversation key="thread" support={support} /> : <Start key="new" support={support} />}
      </div>
    </section>
  );
};

export default SupportWindow;
